import {Divider, Select, Spin} from 'antd'
import Link from 'antd/es/typography/Link'
import {useAppContext} from 'common/context/AppContext'
import {apiCall} from 'common/util/Util'
import {ReactNode, useEffect, useState} from 'react'

const LOADING_MIN_INTERVAL = 200

export interface LabelValue<ID extends number | string> {
    value?: ID
    label?: string
}

interface ReloadableSelectorProps<ID extends number | string> {
    value?: ID[] | ID
    onChange: (selectedValues: any) => void
    size: number
    loadValues: (selectedValues: ID[]) => Promise<LabelValue<ID>[]>
    loadOptions: (search: string, selectedValues: ID[] | undefined, page: number, size: number) => Promise<(LabelValue<ID>|undefined)[]|undefined>
    multiple: boolean
    style?: React.CSSProperties
    placeholder?: string
    disabled?: boolean
    errorText?: string
    onOpenChange?: (open:boolean) => void
}

export const ReloadableSelector = <ID extends number | string>(
    {
        value,
        onChange,
        size,
        loadValues,
        loadOptions,
        multiple,
        style,
        placeholder,
        disabled,
        errorText,
    }: ReloadableSelectorProps<ID>
) => {
    const [options, setOptions] = useState<Array<any>>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [search, setSearch] = useState<string | undefined>(undefined)
    const [selectedValues, setSelectedValues] = useState<undefined | { value: string | number, label?: ReactNode } |
        { value: string | number, label?: ReactNode }[]>()
    const [pageNum, setPageNum] = useState<number>(1)
    const [timeoutId, setTimeoutId] = useState<any>(undefined)
    const appContext = useAppContext()
    const [firstMount, setFirstMount] = useState<boolean>(true)

    function fetchOptions(searchPage: number) {
        if (disabled || search === undefined) { // search is undefined on component load
            return
        }

        setOptions([])
        apiCall(
            setLoading, appContext,
            () => loadOptions(search, [], size, searchPage),
            values => setOptions(
                (values && values.filter(item => item !== undefined)) || []
            ),
            errorText
        )
        setPageNum(searchPage)
    }

    function doSearch(searchPage: number) {
        if (timeoutId) {
            clearTimeout(timeoutId)
        }
        setTimeoutId(setTimeout(() => {
            setTimeoutId(undefined)
            fetchOptions(searchPage)
        }, LOADING_MIN_INTERVAL))
    }

    function arraysEqualIgnoreOrder<T>(arr1: T[], arr2: T[]): boolean {
        if (arr1.length !== arr2.length) return false;
        return arr1.every(val => arr2.includes(val));
    }

    useEffect(() => {
        let noChange = false
        if (value instanceof Array && selectedValues instanceof Array) {
            noChange = arraysEqualIgnoreOrder(value, selectedValues.map(value => value['value']))
        } else if (selectedValues && value && value == selectedValues['value']) {
            noChange = true
        } else if (selectedValues === undefined && value === undefined) {
            noChange = true
        }

        if (!noChange) {
            if (value === undefined) {
                handleChange(undefined)
            } else {
                const valueIsArr = value instanceof Array
                apiCall(setLoading, appContext, () => loadValues(valueIsArr ?  value : [value]), values => {
                    const toSelect = values.map(value => ({
                        value: value.value || 0,
                        label: (value.label || '----') as ReactNode
                    }))
                    handleChange(valueIsArr ? toSelect : (toSelect.length > 0 ? toSelect[0] : undefined))
                }, errorText)
            }
        }
    }, [value])

    useEffect(() => {
        doSearch(1)
    }, [search])

    const handleChange = (values) => {
        setSelectedValues(values)
    }

    useEffect(() => {
        if (firstMount) {
            setFirstMount(false)
            return
        }
        if (selectedValues) {
            if (selectedValues instanceof Array) {
                onChange(selectedValues.map(value => value['value']))
            } else {
                onChange(selectedValues['value'])
            }
        } else {
            onChange(undefined)
        }
    }, [selectedValues]);

    const customRender = (menu: any) =>
        <>
            {menu}{(options.length >= size || pageNum > 1) && <>
            <Divider style={{margin: '8px 0 0 0'}}/>
            <div style={{padding: '0px 8px', textAlign: 'center'}}>
                {pageNum > 0 ?
                    <Link onClick={() => {
                        fetchOptions(pageNum - 1)
                    }} style={{float: 'left'}}>&lt;&lt;</Link> : '  '}
                {(pageNum - 1) * size + 1} - {(pageNum - 1) * size + Math.max(options.length, 1)}
                {options.length >= size ?
                    <Link onClick={() => {
                        fetchOptions(pageNum + 1)
                    }} style={{float: 'right'}}>&gt;&gt;</Link> : '  '}
            </div>
        </>}
        </>

    return <Select
        style={style}
        placeholder={placeholder}
        notFoundContent={loading ? <Spin/> : 'No Data'}
        filterOption={false}
        mode={multiple ? "multiple" : undefined}
        allowClear
        labelInValue
        showSearch
        searchValue={search}
        value={selectedValues}
        onChange={handleChange}
        options={options.filter(bean => search ? bean.label.toLowerCase().indexOf(search.toLowerCase()) !== -1 : true)}
        onSearch={(value) => setSearch(value)}
        onClear={() => handleChange(undefined)}
        onOpenChange={open => {
            if (open) {
                if (search === '') {
                    fetchOptions(1)
                } else {
                    setSearch('')
                }
            }
        }}
        popupRender={customRender}
        loading={loading}
        disabled={disabled}
    />
}

